import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GripVertical, Plus, Pencil, Copy, Trash2, Eye, Loader2, Upload, X, Search, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import {
  DndContext, closestCenter, SortableContext, arrayMove,
  verticalListSortingStrategy, SortableItem, useDndSensors,
} from "../components/Sortable";
import {
  apiGetHomepage, apiSaveHomepageDraft, apiPublishHomepage, apiResetHomepageDefault, apiMeta,
  apiSearchProducts, apiUploadImage, apiFeaturedReviews, errMsg,
} from "../adminApi";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

const TYPE_LABELS = {
  legacy_default: "Homepage attuale",
  block_hero: "Hero + promessa",
  block_categories: "Categorie più cercate",
  block_offers: "Fila offerte",
  block_recommended: "Fila consigliati",
  block_two_grades: "Come nuovo / Usato",
  block_how_we_check: "Come controlliamo i prodotti",
  block_reviews: "Recensioni clienti",
  block_faq: "Domande frequenti (FAQ)",
  hero: "Hero (slide)",
  showcase_manual: "Vetrina manuale",
  showcase_auto: "Vetrina automatica",
  featured_categories: "Categorie in evidenza",
  brands: "Marchi",
  banner: "Banner promozionale",
  benefits: "Fascia vantaggi",
  reviews_featured: "Recensioni in evidenza",
};

// Blocchi nativi: rendono le sezioni originali della homepage, con titoli editabili.
const NATIVE_BLOCK_TYPES = [
  "block_hero", "block_categories", "block_offers", "block_recommended",
  "block_two_grades", "block_how_we_check", "block_reviews", "block_faq",
];
// Blocchi senza campi testuali da modificare (solo attiva/riordina).
const BLOCKS_NO_FIELDS = new Set(["block_two_grades"]);

const newSection = (type) => {
  const base = { id: uid(), type, enabled: true, visible_from: "", visible_to: "" };
  const defaults = {
    block_hero: {},
    block_categories: { title: "Le categorie più cercate" },
    block_offers: { title: "Le migliori offerte", see_all_link: "/offerte" },
    block_recommended: { title: "Consigliati per te", see_all_link: "/catalogo" },
    block_two_grades: {},
    block_how_we_check: { title: "Come controlliamo i prodotti", subtitle: "" },
    block_reviews: { title: "Cosa dicono i nostri clienti", subtitle: "Migliaia di ordini consegnati in tutta Italia." },
    block_faq: { title: "Le domande più frequenti" },
    hero: { slides: [{ id: uid(), image_desktop: "", image_mobile: "", title: "", subtitle: "", cta_label: "", link: "" }] },
    showcase_manual: { title: "", subtitle: "", product_ids: [], products_meta: [], layout: "grid", see_all_link: "" },
    showcase_auto: { title: "", rule: "featured", rule_value: "", count: 8, sort: "novita" },
    featured_categories: { category_keys: [] },
    brands: { brand_keys: [] },
    banner: { image_desktop: "", image_mobile: "", title: "", text: "", cta_label: "", link: "" },
    benefits: { items: [{ title: "", text: "" }, { title: "", text: "" }] },
    reviews_featured: { title: "Cosa dicono i clienti", review_ids: [] },
    legacy_default: {},
  };
  return { ...base, ...(defaults[type] || {}) };
};

function ImageField({ label, value, onChange, cloudinaryOk, folder = "rinova/homepage", context = "" }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!cloudinaryOk) { toast.error("Cloudinary non configurato: incolla un URL."); return; }
    setBusy(true);
    try { const res = await apiUploadImage(file, folder, undefined, context); onChange(res.url); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL immagine o carica" className="flex-1" />
        <label>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} />
          <Button asChild variant="secondary" size="icon"><span className="cursor-pointer">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}</span></Button>
        </label>
      </div>
      {value && <img src={value} alt="" className="mt-1 h-16 w-full rounded-md object-cover" />}
    </div>
  );
}

function ProductPicker({ ids, meta, onChange }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (!q) { setRes([]); return; }
    const t = setTimeout(() => apiSearchProducts(q).then(setRes).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotti" className="pl-9" />
      </div>
      {res.length > 0 && (
        <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-line bg-surface3">
          {res.map((r) => (
            <button key={r.id} className="block w-full px-3 py-2 text-left text-sm text-chalk hover:bg-surface"
              onClick={() => { if (!meta.find((m) => m.id === r.id)) onChange([...meta, { id: r.id, name: r.name }]); setQ(""); setRes([]); }}>
              {r.name}
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {meta.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-surface3 px-2 py-1 text-xs text-chalk">
            {p.name}<button onClick={() => onChange(meta.filter((x) => x.id !== p.id))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function SectionEditor({ section, setSection, meta, cloudinaryOk }) {
  const s = section;
  const up = (patch) => setSection({ ...s, ...patch });
  if (s.type === "legacy_default") return <p className="text-sm text-steel">Questa sezione mostra la homepage attuale così com'è. Non ha campi da modificare.</p>;

  // --- Blocchi nativi (sezioni originali della homepage) ---
  if (BLOCKS_NO_FIELDS.has(s.type)) {
    return <p className="text-sm text-steel">Questo blocco riprende la sezione originale della homepage. Puoi attivarlo/disattivarlo e riordinarlo; non ha testi da modificare.</p>;
  }
  if (s.type === "block_categories" || s.type === "block_faq") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5"><Label>Titolo</Label>
          <Input placeholder="Titolo sezione" value={s.title || ""} onChange={(e) => up({ title: e.target.value })} /></div>
      </div>
    );
  }
  if (s.type === "block_offers" || s.type === "block_recommended") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5"><Label>Titolo</Label>
          <Input placeholder="Titolo fila" value={s.title || ""} onChange={(e) => up({ title: e.target.value })} /></div>
        <div className="flex flex-col gap-1.5"><Label>Link "Vedi tutti"</Label>
          <Input placeholder="/offerte, /catalogo…" value={s.see_all_link || ""} onChange={(e) => up({ see_all_link: e.target.value })} /></div>
        <p className="text-xs text-steel">I prodotti mostrati sono automatici {s.type === "block_offers" ? "(prodotti in offerta)" : "(prodotti in evidenza e catalogo)"}.</p>
      </div>
    );
  }
  if (s.type === "block_how_we_check" || s.type === "block_reviews") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5"><Label>Titolo</Label>
          <Input placeholder="Titolo sezione" value={s.title || ""} onChange={(e) => up({ title: e.target.value })} /></div>
        <div className="flex flex-col gap-1.5"><Label>Sottotitolo</Label>
          <Textarea placeholder="Sottotitolo (facolt.)" value={s.subtitle || ""} onChange={(e) => up({ subtitle: e.target.value })} rows={2} /></div>
      </div>
    );
  }

  if (s.type === "block_hero") {
    const slides = s.slides || [];
    const upSlide = (i, patch) => up({ slides: slides.map((sl, idx) => (idx === i ? { ...sl, ...patch } : sl)) });
    return (
      <div className="flex flex-col gap-4">
        <p className="text-xs text-steel">Se non aggiungi slide, resta il design predefinito dell'hero. Le foto vengono ottimizzate automaticamente (16:6, WebP).</p>
        {slides.map((sl, i) => (
          <div key={sl.id} className="rounded-lg border border-line p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-sm font-medium text-chalk">Slide {i + 1}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => up({ slides: slides.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button></div>
            <div className="grid gap-3">
              <ImageField label="Immagine (16:6)" value={sl.image} onChange={(v) => upSlide(i, { image: v })} cloudinaryOk={cloudinaryOk} context="hero" />
              <Input placeholder="Titolo" value={sl.title || ""} onChange={(e) => upSlide(i, { title: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Testo pulsante" value={sl.cta_label || ""} onChange={(e) => upSlide(i, { cta_label: e.target.value })} />
                <Input placeholder="Link (/catalogo, /offerte…)" value={sl.link || ""} onChange={(e) => upSlide(i, { link: e.target.value })} />
              </div>
            </div>
          </div>
        ))}
        {slides.length < 5 && <Button variant="secondary" size="sm" className="self-start" onClick={() => up({ slides: [...slides, { id: uid(), image: "", title: "", cta_label: "", link: "/catalogo" }] })}><Plus className="h-4 w-4" /> Aggiungi slide</Button>}
      </div>
    );
  }

  if (s.type === "hero") {
    const slides = s.slides || [];
    const upSlide = (i, patch) => up({ slides: slides.map((sl, idx) => idx === i ? { ...sl, ...patch } : sl) });
    return (
      <div className="flex flex-col gap-4">
        {slides.map((sl, i) => (
          <div key={sl.id} className="rounded-lg border border-line p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-sm font-medium text-chalk">Slide {i + 1}</span>
              {slides.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => up({ slides: slides.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button>}</div>
            <div className="grid gap-3">
              <ImageField label="Immagine desktop (16:6)" value={sl.image_desktop} onChange={(v) => upSlide(i, { image_desktop: v })} cloudinaryOk={cloudinaryOk} />
              <ImageField label="Immagine mobile (4:5)" value={sl.image_mobile} onChange={(v) => upSlide(i, { image_mobile: v })} cloudinaryOk={cloudinaryOk} />
              <Input placeholder="Titolo" value={sl.title} onChange={(e) => upSlide(i, { title: e.target.value })} />
              <Input placeholder="Sottotitolo" value={sl.subtitle} onChange={(e) => upSlide(i, { subtitle: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Testo pulsante" value={sl.cta_label} onChange={(e) => upSlide(i, { cta_label: e.target.value })} />
                <Input placeholder="Link (/catalogo, /offerte…)" value={sl.link} onChange={(e) => upSlide(i, { link: e.target.value })} />
              </div>
            </div>
          </div>
        ))}
        {slides.length < 3 && <Button variant="secondary" size="sm" className="self-start" onClick={() => up({ slides: [...slides, { id: uid(), image_desktop: "", image_mobile: "", title: "", subtitle: "", cta_label: "", link: "" }] })}><Plus className="h-4 w-4" /> Aggiungi slide</Button>}
      </div>
    );
  }
  if (s.type === "showcase_manual") {
    return (
      <div className="flex flex-col gap-3">
        <Input placeholder="Titolo" value={s.title} onChange={(e) => up({ title: e.target.value })} />
        <Input placeholder="Sottotitolo" value={s.subtitle} onChange={(e) => up({ subtitle: e.target.value })} />
        <div><Label className="mb-1 block">Prodotti (trascina nel sito nell'ordine scelto)</Label>
          <ProductPicker meta={s.products_meta || []} onChange={(m) => up({ products_meta: m, product_ids: m.map((x) => x.id) })} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5"><Label>Formato</Label>
            <Select value={s.layout} onValueChange={(v) => up({ layout: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="grid">Griglia</SelectItem><SelectItem value="rail">Fila scorrevole</SelectItem></SelectContent></Select></div>
          <Input placeholder='Link "Vedi tutti" (facolt.)' value={s.see_all_link} onChange={(e) => up({ see_all_link: e.target.value })} />
        </div>
      </div>
    );
  }
  if (s.type === "showcase_auto") {
    return (
      <div className="flex flex-col gap-3">
        <Input placeholder="Titolo" value={s.title} onChange={(e) => up({ title: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col gap-1.5"><Label>Regola</Label>
            <Select value={s.rule} onValueChange={(v) => up({ rule: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="featured">In evidenza</SelectItem><SelectItem value="on_sale">In offerta</SelectItem><SelectItem value="new">Novità</SelectItem>
              <SelectItem value="cheapest">Più economici</SelectItem><SelectItem value="category">Categoria</SelectItem><SelectItem value="brand">Marchio</SelectItem>
            </SelectContent></Select></div>
          {(s.rule === "category" || s.rule === "brand") && (
            <div className="flex flex-col gap-1.5"><Label>Valore</Label>
              <Select value={s.rule_value} onValueChange={(v) => up({ rule_value: v })}><SelectTrigger><SelectValue placeholder="Scegli" /></SelectTrigger><SelectContent>
                {(s.rule === "category" ? meta.categories : meta.brands).map((o) => <SelectItem key={o.key} value={o.key}>{o.label || o.name}</SelectItem>)}
              </SelectContent></Select></div>
          )}
          <div className="flex flex-col gap-1.5"><Label>Numero prodotti</Label><Input type="number" value={s.count} onChange={(e) => up({ count: Number(e.target.value) })} /></div>
          <div className="flex flex-col gap-1.5"><Label>Ordinamento</Label>
            <Select value={s.sort} onValueChange={(v) => up({ sort: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="novita">Novità</SelectItem><SelectItem value="prezzo_asc">Prezzo crescente</SelectItem><SelectItem value="prezzo_desc">Prezzo decrescente</SelectItem>
            </SelectContent></Select></div>
        </div>
      </div>
    );
  }
  if (s.type === "featured_categories" || s.type === "brands") {
    const isCat = s.type === "featured_categories";
    const opts = isCat ? meta.categories : meta.brands;
    const key = isCat ? "category_keys" : "brand_keys";
    const sel = s[key] || [];
    const toggle = (k) => up({ [key]: sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k] });
    return (
      <div className="flex flex-col gap-2">
        <Label>Scegli e ordina</Label>
        <div className="flex flex-wrap gap-2">
          {opts.map((o) => (
            <button key={o.key} onClick={() => toggle(o.key)}
              className={`rounded-full border px-3 py-1.5 text-sm ${sel.includes(o.key) ? "border-lime/50 bg-surface3 text-lime" : "border-line text-steel"}`}>
              {o.label || o.name}{sel.includes(o.key) && ` · ${sel.indexOf(o.key) + 1}`}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (s.type === "banner") {
    return (
      <div className="flex flex-col gap-3">
        <ImageField label="Immagine desktop" value={s.image_desktop} onChange={(v) => up({ image_desktop: v })} cloudinaryOk={cloudinaryOk} />
        <ImageField label="Immagine mobile" value={s.image_mobile} onChange={(v) => up({ image_mobile: v })} cloudinaryOk={cloudinaryOk} />
        <Input placeholder="Titolo" value={s.title} onChange={(e) => up({ title: e.target.value })} />
        <Textarea placeholder="Testo" value={s.text} onChange={(e) => up({ text: e.target.value })} rows={2} />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Testo pulsante" value={s.cta_label} onChange={(e) => up({ cta_label: e.target.value })} />
          <Input placeholder="Link" value={s.link} onChange={(e) => up({ link: e.target.value })} />
        </div>
      </div>
    );
  }
  if (s.type === "benefits") {
    const items = s.items || [];
    const upItem = (i, patch) => up({ items: items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
    return (
      <div className="flex flex-col gap-3">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input placeholder="Titolo (es. Garanzia 12 mesi)" value={it.title} onChange={(e) => upItem(i, { title: e.target.value })} className="flex-1" />
            <Input placeholder="Testo breve" value={it.text} onChange={(e) => upItem(i, { text: e.target.value })} className="flex-1" />
            {items.length > 2 && <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => up({ items: items.filter((_, idx) => idx !== i) })}><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
        {items.length < 4 && <Button variant="secondary" size="sm" className="self-start" onClick={() => up({ items: [...items, { title: "", text: "" }] })}><Plus className="h-4 w-4" /> Aggiungi punto</Button>}
      </div>
    );
  }
  if (s.type === "reviews_featured") {
    const sel = s.review_ids || [];
    const toggle = (id) => up({ review_ids: sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id] });
    return (
      <div className="flex flex-col gap-3">
        <Input placeholder="Titolo" value={s.title} onChange={(e) => up({ title: e.target.value })} />
        <Label>Recensioni in evidenza (tocca per includere e ordinare)</Label>
        {(meta.featuredReviews || []).length === 0 ? (
          <p className="text-sm text-steel">Nessuna recensione segnata "in evidenza". Attiva "In evidenza" su una recensione.</p>
        ) : (
          <div className="flex flex-col gap-1.5">
            {(meta.featuredReviews || []).map((r) => (
              <button key={r.id} onClick={() => toggle(r.id)}
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${sel.includes(r.id) ? "border-lime/50 bg-surface3 text-chalk" : "border-line text-steel"}`}>
                <span className="truncate">{"★".repeat(r.stars)} · {r.title || r.display_name} — {r.product_name}</span>
                {sel.includes(r.id) && <span className="ml-2 text-xs text-lime">#{sel.indexOf(r.id) + 1}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default function HomepageBuilder() {
  const sensors = useDndSensors();
  const [sections, setSections] = useState(null);
  const [meta, setMeta] = useState({ brands: [], categories: [], cloudinary_configured: false });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiMeta().then((m) => {
      apiFeaturedReviews().then((fr) => setMeta({ ...m, featuredReviews: fr })).catch(() => setMeta({ ...m, featuredReviews: [] }));
    }).catch(() => {});
    apiGetHomepage().then((d) => setSections(d.draft.sections || [])).catch((e) => toast.error(errMsg(e)));
  }, []);

  const saveDraft = async (next, silent) => {
    setSections(next);
    setSaving(true);
    try { await apiSaveHomepageDraft(next); if (!silent) toast.success("Bozza salvata"); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    try { await apiSaveHomepageDraft(sections); await apiPublishHomepage(); toast.success("Homepage pubblicata"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const resetDefault = async () => {
    if (!window.confirm("Ripristinare la homepage al layout predefinito? Le sezioni personalizzate verranno sostituite dai blocchi originali (bozza e pubblicato).")) return;
    try {
      const res = await apiResetHomepageDefault();
      setSections(res.sections || []);
      toast.success("Layout predefinito ripristinato e pubblicato");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oi = sections.findIndex((s) => s.id === active.id);
    const ni = sections.findIndex((s) => s.id === over.id);
    saveDraft(arrayMove(sections, oi, ni), true);
  };

  const addSection = (type) => { const sec = newSection(type); saveDraft([...(sections || []), sec], true); setEditing(sec); };
  const duplicate = (sec) => saveDraft([...sections, { ...JSON.parse(JSON.stringify(sec)), id: uid() }], true);
  const remove = (id) => saveDraft(sections.filter((s) => s.id !== id), true);
  const toggle = (id, v) => saveDraft(sections.map((s) => s.id === id ? { ...s, enabled: v } : s), true);
  const commitEdit = () => { saveDraft(sections.map((s) => s.id === editing.id ? editing : s)); setEditing(null); };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Homepage</h1>
          <p className="mt-1 text-sm text-steel">Componi le sezioni. Le modifiche restano in bozza finché non pubblichi.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/?preview=1" target="_blank" rel="noreferrer"><Button variant="secondary" size="sm" data-testid="homepage-preview-button"><Eye className="h-4 w-4" /> Anteprima</Button></a>
          <Button variant="ghost" size="sm" onClick={resetDefault} data-testid="homepage-reset-default"><RotateCcw className="h-4 w-4" /> Ripristina layout</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="secondary" size="sm" data-testid="homepage-add-section"><Plus className="h-4 w-4" /> Aggiungi sezione</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Blocchi homepage</DropdownMenuLabel>
              {NATIVE_BLOCK_TYPES.map((t) => (
                <DropdownMenuItem key={t} onClick={() => addSection(t)}>{TYPE_LABELS[t]}</DropdownMenuItem>
              ))}
              <DropdownMenuLabel>Sezioni personalizzate</DropdownMenuLabel>
              {["hero", "showcase_manual", "showcase_auto", "featured_categories", "brands", "banner", "benefits", "reviews_featured"].map((t) => (
                <DropdownMenuItem key={t} onClick={() => addSection(t)}>{TYPE_LABELS[t]}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="lime" size="sm" onClick={publish} disabled={saving} data-testid="homepage-publish-button">Pubblica</Button>
        </div>
      </div>

      <Card className="border-line bg-surface2">
        <CardContent className="p-3">
          {!sections ? <p className="p-4 text-sm text-steel">Caricamento…</p> : sections.length === 0 ? (
            <p className="py-8 text-center text-sm text-steel">Nessuna sezione. Aggiungine una.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col gap-2" data-testid="homepage-sections-list">
                  {sections.map((s) => (
                    <SortableItem key={s.id} id={s.id}>
                      {({ setNodeRef, style, attributes, listeners }) => (
                        <div ref={setNodeRef} style={style} {...attributes} className="flex items-center gap-3 rounded-lg border border-line bg-surface3 px-3 py-2.5">
                          <button className="cursor-grab text-tertiary" {...listeners} aria-label="Trascina"><GripVertical className="h-4 w-4" /></button>
                          <div className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-chalk">{TYPE_LABELS[s.type]}</span>
                            <span className="text-xs text-steel">{s.title || s.type}{s.visible_from || s.visible_to ? " · programmata" : ""}</span>
                          </div>
                          <Switch checked={s.enabled !== false} onCheckedChange={(v) => toggle(s.id, v)} aria-label="Attiva sezione" />
                          {s.type !== "legacy_default" && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(s)} aria-label="Modifica"><Pencil className="h-4 w-4" /></Button>}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicate(s)} aria-label="Duplica"><Copy className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => remove(s.id)} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </SortableItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing && TYPE_LABELS[editing.type]}</DialogTitle></DialogHeader>
          {editing && (
            <div className="flex flex-col gap-4">
              <SectionEditor section={editing} setSection={setEditing} meta={meta} cloudinaryOk={meta.cloudinary_configured} />
              <div className="grid grid-cols-2 gap-3 border-t border-line pt-3">
                <div className="flex flex-col gap-1.5"><Label>Visibile dal (facolt.)</Label><Input type="datetime-local" value={editing.visible_from ? editing.visible_from.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, visible_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} /></div>
                <div className="flex flex-col gap-1.5"><Label>Visibile al (facolt.)</Label><Input type="datetime-local" value={editing.visible_to ? editing.visible_to.slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, visible_to: e.target.value ? new Date(e.target.value).toISOString() : "" })} /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)}>Annulla</Button>
            <Button variant="lime" onClick={commitEdit} data-testid="homepage-section-save">Salva sezione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
